/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  createAsyncThunk,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit';

export const CCF_DIRECTORY_FEATURE_KEY = 'agentDirectory';

export enum AgentStatus {
  WORKING = 'working',
  AVAILABLE = 'available',
  BUSY = 'busy',
  OFFLINE = 'offline',
}

export interface Agent {
  id: number;
  name: string;
  status: AgentStatus.AVAILABLE | AgentStatus.WORKING | AgentStatus.BUSY | AgentStatus.OFFLINE;
  designation: 'Agent Supervisor' | 'Technical Support Agent';
  isFavourite?: boolean;
  contactNo?: string;
}

export interface AgentGroup {
  id: number;
  name: string;
  status: AgentStatus.AVAILABLE | AgentStatus.WORKING;
  designation: 'Agent Supervisor' | 'Technical Support Agent';
}

export interface CcfDirectoryState {
  query: {
    searchBox: string;
    dropDown: string;
  };
  skills?: { [key: string]: string[] };
  isLoading: boolean;
  agentContacts: Agent[];
  agentGroups: AgentGroup[];
}

export const initialCcfDirectoryState: CcfDirectoryState = {
  query: {
    searchBox: '',
    dropDown: 'my_team',
  },
  skills: {
    '221-232-454': ['React', 'Angular'],
    '432-572-123': ['React'],
  },
  isLoading: true,
  agentContacts: [],
  agentGroups: [],
};

export const fetchAllAgentContacts = createAsyncThunk(
  'agentDirectory/fetchAllAgentContacts',
  async (args, { getState }: { getState: () => any }) => {
    const simulatorAgentDirectory = getState().simulator.agentDirectory;
    const data = [
      ...simulatorAgentDirectory.myTeam,
      ...simulatorAgentDirectory.favourites,
    ];
    const agentContacts = new Promise((resolve, reject) =>
      setTimeout(() => resolve(data), 1000)
    );
    await agentContacts;
    return agentContacts;
  }
);

export const fetchAgentContacts = createAsyncThunk(
  'agentDirectory/fetchAgentContacts',
  async (args, { getState }: { getState: () => any }) => {
    const data = getState().simulator.agentDirectory.myTeam;
    const agentContacts = new Promise((resolve, reject) =>
      setTimeout(() => resolve(data), 1000)
    );
    await agentContacts;
    return agentContacts;
  }
);

export const fetchAgentGroups = createAsyncThunk(
  'agentDirectory/fetchAgentGroups',
  async (args, { getState }: { getState: () => any }) => {
    const data = getState().simulator.agentDirectory.agentGroups;
    const agentGroups = new Promise((resolve, reject) =>
      setTimeout(() => resolve(data), 1000)
    );
    await agentGroups;
    return agentGroups;
  }
);

export const searchAgentContacts = createAsyncThunk(
  'agentDirectory/searchAgentContacts',
  async (value: string, { getState }: { getState: () => any }) => {
    const { query } = getState().agentDirectory;
    const data = getState().simulator.agentDirectory.myTeam;
    const people = getState().simulator.agentDirectory.people;
    const favourites = getState().simulator.agentDirectory.favourites;
    const filteredData: Agent[] = value
      ? query.dropDown === 'favourites'
        ? favourites.filter(
            (_: Agent) =>
              _.name.toLowerCase().includes(value) || _.contactNo === value
          )
        : data.filter(
            (_: Agent) =>
              _.name.toLowerCase().includes(value) || _.contactNo === value
          )
      : query.dropDown === 'favourites'
      ? favourites
      : query.dropDown === 'people'
      ? people
      : data;
    const response = new Promise<Agent[]>((resolve, reject) =>
      setTimeout(() => resolve(filteredData), 1000)
    );
    await response;
    return response;
  }
);

export const fetchAgentContactGroup = createAsyncThunk(
  'agentDirectory/fetchAgentContactGroup',
  async (value: string, { getState }: { getState: () => any }) => {
    const data = getState().simulator.agentDirectory.myTeam;
    const favourites = getState().simulator.agentDirectory.favourites;
    const contacts = value === 'favourites' ? favourites : data;
    const agentContacts = new Promise<Agent[]>((resolve, reject) =>
      setTimeout(() => resolve(contacts as Agent[]), 1000)
    );
    const response = await agentContacts;
    return { agentContacts: response, value };
  }
);

export const directorySlice = createSlice({
  name: CCF_DIRECTORY_FEATURE_KEY,
  initialState: initialCcfDirectoryState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllAgentContacts.fulfilled, (state, action) => {
        return {
          ...state,
          isLoading: false,
          agentContacts: [...(action.payload as Agent[])],
        };
      })
      .addCase(fetchAgentContacts.fulfilled, (state, action) => {
        return {
          ...state,
          isLoading: false,
          agentContacts: [...(action.payload as Agent[])],
        };
      })
      .addCase(fetchAgentGroups.fulfilled, (state, action) => {
        return {
          ...state,
          isLoading: false,
          agentGroups: [...(action.payload as AgentGroup[])],
        };
      })
      .addCase(searchAgentContacts.pending, (state, action) => {
        return {
          ...state,
          query: {
            ...state.query,
            searchBox: action.meta.arg,
          },
          isLoading: true,
        };
      })
      .addCase(searchAgentContacts.fulfilled, (state, action) => {
        return {
          ...state,
          isLoading: false,
          query: {
            ...state.query,
            searchBox: action.meta.arg,
          },
          agentContacts: [...action.payload],
        };
      })
      .addCase(fetchAgentContactGroup.pending, (state, action) => {
        return {
          ...state,
          isLoading: true,
          query: {
            ...state.query,
            dropDown: action.meta.arg,
          },
        };
      })
      .addCase(fetchAgentContactGroup.fulfilled, (state, action) => {
        const filteredGroupContacts = action.payload.agentContacts.filter((_) =>
          _.name.toLowerCase().includes(state.query.searchBox)
        );

        return {
          ...state,
          isLoading: false,
          agentContacts: [...filteredGroupContacts],
        };
      });
  },
});

export const agentDirectoryReducer = directorySlice.reducer;
export const agentDirectoryActions = directorySlice.actions;

/**
 * Function to get Directory data
 * @param rootState - CcfDirectoryState
 * @example
 * @returns It returns Directory data
 */
const getCcfDirectoryState = (rootState: {
  agentDirectory: CcfDirectoryState;
}) => {
  return rootState[CCF_DIRECTORY_FEATURE_KEY];
};

export const selectAgentList = createSelector(
  getCcfDirectoryState,
  (state) => state.agentContacts
);
export const selectAgentGroupList = createSelector(
  getCcfDirectoryState,
  (state) => state.agentGroups
);
export const selectLoadingStatus = createSelector(
  getCcfDirectoryState,
  (state) => state.isLoading
);
export const selectDropDownQueryValue = createSelector(
  getCcfDirectoryState,
  (state) => state.query.dropDown
);
export const selectSearchBoxQueryValue = createSelector(
  getCcfDirectoryState,
  (state) => state.query.searchBox
);
export const selectSkillsValue = createSelector(
  getCcfDirectoryState,
  (state) => state.skills
);